import {
  extend, arrayOf, params, shape, string, number, bool, client, alias, customType
} from '@thd-nucleus/data-sources';
import { Price } from '@thd-olt-component-react/price';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { ProductHeader } from '@thd-olt-component-react/product-details';

import { CurrentItem } from '../current-item/CurrentItem';
import { ProductPodWrapper } from '../elligible-items/product-pod/ProductPodWrapper';

// Shared by packageSwapRecs and searchModel so both sources render the same product pods
const swapProduct = () => extend({
  itemId: string(),
  availabilityType: shape({
    buyable: bool(),
    discontinued: bool(),
    status: bool(),
    type: string(),
  }),
  dataSource: string(),
  identifiers: shape({
    itemId: string(),
    canonicalUrl: string(),
    productLabel: string(),
    brandName: string(),
    modelNumber: string(),
    productType: string(),
    storeSkuNumber: string()
  }),
  info: shape({
    fiscalYear: string(),
    classNumber: string(),
    isLiveGoodsProduct: bool()
  }),
  badges: shape({
    label: string(),
    message: string(),
    name: string(),
  }),
  reviews: shape({
    ratingsReviews: shape({
      averageRating: number(),
      totalReviews: number()
    })
  }),
  pricing: params({ storeId: string() }).shape({
    value: number(),
    original: number({ float: true }),
    promotion: shape({
      dates: shape({
        start: string(),
        end: string()
      }),
      promotionTag: string(),
      promoId: string()
    }),
    mapDetail: shape({
      mapPolicy: string(),
      mapSpecialPriceViolation: bool(),
      mapOriginalPriceViolation: bool(),
      percentageOff: number({ float: true }),
      effectiveMap: number({ float: true }),
      dollarOff: number({ float: true })
    })
  }),
  fulfillment: client(params({ storeId: string(), zipCode: string() }).shape({
    fulfillmentOptions: arrayOf(shape({
      services: arrayOf(shape({
        type: string(),
        locations: arrayOf(shape({
          isAnchor: bool(),
          inventory: shape({
            quantity: number()
          })
        })),
      }))
    }))
  })),
  media: shape({
    image: shape({
      url: string()
    }).client(),
    images: arrayOf({
      subType: string(),
      url: string(),
      type: string(),
      sizes: arrayOf(string())
    })
  })
}, Price.dataModel.product, ProductPodWrapper.dataModel.product);

const dataModel = extend({
  packageSwapRecs: params({
    itemId: string().isRequired(),
    storeId: string(),
    promoId: string(),
    filterResponse: bool()
  }).shape({
    products: params().arrayOf(shape(swapProduct())),
  }),
  // Used by the fitOptions flow instead of packageSwapRecs. storefilter ALL keeps storeId from
  // limiting results to in-store inventory, which would drop nearly all ship-to-home appliances.
  searchModel: params({
    navParam: string(),
    storeId: string(),
    storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL')
  }).shape({
    searchReport: shape({
      totalProducts: number()
    }),
    products: arrayOf(shape(swapProduct()))
  }),
  // fitOptions: size facets for the base navParam, used to scope the product search to size
  // buckets that can fit the shopper's space
  fitOptionsSizeFacets: alias('searchModel').params({
    navParam: string(),
    storeId: string(),
    storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL')
  }).shape({
    dimensions: arrayOf(shape({
      dimensionId: string(),
      label: string(),
      refinements: arrayOf(shape({
        label: string(),
        refinementKey: string(),
        recordCount: string()
      }))
    }))
  }),
  // fitOptions: specifications for the search results (search does not return them) and the current
  // item. Exact dimensions filter the results; finish, price and brand sort them against the current item.
  fitOptionsProductSpecs: alias('products').params({
    itemIds: arrayOf(string().isRequired()).isRequired()
  }).arrayOf(shape({
    itemId: string(),
    identifiers: shape({
      brandName: string()
    }),
    pricing: params({ storeId: string() }).shape({
      value: number({ float: true })
    }),
    specificationGroup: arrayOf(shape({
      specTitle: string(),
      specifications: arrayOf(shape({
        specName: string(),
        specValue: string()
      }))
    }))
  })),
  outOfStockRecs: alias('recs').params({
    anchorId: string().isRequired(),
    apiName: string().isRequired(),
    appId: string(),
    storeId: string(),
    key: string(),
    configOverrides: arrayOf(customType('ConfigOverride').shape({
      configName: string(),
      configValue: string()
    }))
  }).shape({
    products: arrayOf(shape({
      isAnchor: bool(),
      product: params({ dataSource: string() }).shape({
        itemId: string(),
        dataSource: string()
      })
    }))
  }),
  outOfStockRecsAvailability: alias('checkAvailability').params({
    itemIds: arrayOf(string().isRequired()).isRequired(),
    zipCode: string().isRequired(),
    localStoreId: string().isRequired()
  }).shape({
    fulfillmentOptions: arrayOf(shape({
      services: arrayOf(shape({
        deliveryAvailabilityDetail: arrayOf(shape({
          itemId: string(),
          status: string()
        }))
      }))
    }))
  }),
  outOfStockRecsProducts: alias('products').params({
    itemIds: arrayOf(string().isRequired()).isRequired()
  }).arrayOf(shape(extend({
    itemId: string(),
    dataSource: string()
  }, ProductPodWrapper.dataModel.product))),
  outOfStockCurrentItemSpecifications: alias('product').params({
    itemId: string().isRequired()
  }).shape({
    itemId: string(),
    dataSource: string(),
    specificationGroup: arrayOf(shape({
      specTitle: string(),
      specifications: arrayOf(shape({
        specName: string(),
        specValue: string()
      }))
    }))
  })
}, CurrentItem, ProductPodWrapper.dataModel.product);
delete dataModel.packageSwapRecs.products._query;

export { dataModel };
